import { Injectable } from "@angular/core";

export type NogoshClientBrowser = "Chrome" | "Safari" | "Firefox" | "Edge" | "Other";
export type NogoshClientOs = "macOS" | "Windows" | "iOS" | "Android" | "Linux" | "Other";
export type NogoshDeviceType = "desktop" | "mobile" | "tablet";

export interface AnalyticsClientContext {
  platform: string;
  sessionId: string;
  anonymousId: string;
  deviceId: string;
  browser: NogoshClientBrowser;
  os: NogoshClientOs;
  deviceType: NogoshDeviceType;
  clientVersion?: string;
  webCommit?: string;
}

@Injectable({ providedIn: "root" })
export class AnalyticsClientContextService {
  private static readonly DEVICE_ID_KEY = "nogosh.analytics.deviceId";
  private static readonly ANONYMOUS_ID_KEY = "nogosh.analytics.anonymousId";
  private static readonly SESSION_ID_KEY = "nogosh.analytics.sessionId";
  private static readonly VERSION = "0.9.8";

  private readonly sessionId: string;
  private readonly deviceId: string;
  private readonly anonymousId: string;
  private readonly browser: NogoshClientBrowser;
  private readonly os: NogoshClientOs;
  private readonly deviceType: NogoshDeviceType;
  private readonly webCommit: string;

  constructor() {
    this.sessionId = this.getSessionValue(AnalyticsClientContextService.SESSION_ID_KEY);
    this.deviceId = this.getLocalValue(AnalyticsClientContextService.DEVICE_ID_KEY);
    this.anonymousId = this.getLocalValue(AnalyticsClientContextService.ANONYMOUS_ID_KEY);
    this.browser = this.detectBrowser();
    this.os = this.detectOs();
    this.deviceType = this.detectDeviceType();
    this.webCommit = this.detectWebCommit();
  }

  public getContext(): AnalyticsClientContext {
    return {
      platform: "web",
      sessionId: this.sessionId,
      anonymousId: this.anonymousId,
      deviceId: this.deviceId,
      browser: this.browser,
      os: this.os,
      deviceType: this.deviceType,
      clientVersion: AnalyticsClientContextService.VERSION,
      webCommit: this.webCommit
    };
  }

  public getHeaders(): {[name: string]: string} {
    const context = this.getContext();
    const headers: {[name: string]: string} = {
      "X-Nogosh-Client-Platform": context.platform,
      "X-Nogosh-Client-Version": context.clientVersion || "",
      "X-Nogosh-Device-Id": context.deviceId,
      "X-Nogosh-Session-Id": context.sessionId,
      "X-Nogosh-Anonymous-Id": context.anonymousId,
      "X-Nogosh-Client-Browser": context.browser,
      "X-Nogosh-Client-Os": context.os,
      "X-Nogosh-Device-Type": context.deviceType
    };

    if (context.webCommit) {
      headers["X-Nogosh-Web-Commit"] = context.webCommit;
    }

    return headers;
  }

  private getLocalValue(key: string): string {
    return this.getStoredValue(localStorage, key);
  }

  private getSessionValue(key: string): string {
    return this.getStoredValue(sessionStorage, key);
  }

  private getStoredValue(storage: Storage, key: string): string {
    let value = storage.getItem(key);
    if (!value) {
      value = this.createId();
      storage.setItem(key, value);
    }
    return value;
  }

  private createId(): string {
    const cryptoRef = window.crypto as Crypto;
    if (cryptoRef && cryptoRef.randomUUID) {
      return cryptoRef.randomUUID();
    }

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
      const random = Math.random() * 16 | 0;
      const value = char === "x" ? random : (random & 0x3 | 0x8);
      return value.toString(16);
    });
  }

  private detectBrowser(): NogoshClientBrowser {
    const userAgent = navigator.userAgent || "";
    if (userAgent.indexOf("Edg/") >= 0 || userAgent.indexOf("Edge/") >= 0) {
      return "Edge";
    }
    if (userAgent.indexOf("Firefox/") >= 0) {
      return "Firefox";
    }
    if (userAgent.indexOf("Chrome/") >= 0 || userAgent.indexOf("CriOS/") >= 0) {
      return "Chrome";
    }
    if (userAgent.indexOf("Safari/") >= 0) {
      return "Safari";
    }
    return "Other";
  }

  private detectOs(): NogoshClientOs {
    const userAgent = navigator.userAgent || "";
    const platform = navigator.platform || "";
    if (/iPhone|iPad|iPod/.test(userAgent) || /iPhone|iPad|iPod/.test(platform)) {
      return "iOS";
    }
    if (userAgent.indexOf("Android") >= 0) {
      return "Android";
    }
    if (platform.indexOf("Mac") >= 0) {
      return "macOS";
    }
    if (platform.indexOf("Win") >= 0) {
      return "Windows";
    }
    if (platform.indexOf("Linux") >= 0) {
      return "Linux";
    }
    return "Other";
  }

  private detectDeviceType(): NogoshDeviceType {
    const userAgent = navigator.userAgent || "";
    if (/iPad|Tablet/.test(userAgent)) {
      return "tablet";
    }
    if (/Mobi|Android|iPhone|iPod/.test(userAgent)) {
      return "mobile";
    }
    return "desktop";
  }

  private detectWebCommit(): string {
    const windowRef = window as any;
    const commit = windowRef.NOGOSH_WEB_COMMIT || windowRef.__NOGOSH_WEB_COMMIT__ || '[[WEB_COMMIT_ID]]';
    return /^[a-f0-9]{40}$/i.test(commit) ? commit : "";
  }
}
